import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import {
  AvailabilityStatusSelect,
  BellIcon,
  Button,
  CalendarIcon,
  ChartTrendingUpIcon,
  CheckCircleIcon,
  CoinIcon,
  ConfirmationDialog,
  CountrySelect,
  DateInput,
  DocumentTextIcon,
  DownloadIcon,
  FolderIcon,
  Label,
  ManningAgentSelect,
  MapPinIcon,
  MaritalStatusSelect,
  NationalitySelect,
  NumberInput,
  PlusIcon,
  PortSelect,
  RankSelect,
  RefreshIcon,
  ReligionSelect,
  SexTypeSelect,
  ShipIcon,
  StarIcon,
  Tabs,
  TabsContent,
  Textarea,
  TextInput,
  TrashIcon,
  UploadCloudIcon,
  UserIcon,
  UserPlusIcon,
  type LookupSelectProps,
} from '../../components/ui';
import api from '../../services/api';
import type { CrewFormState } from './formMappers';
import { formToDashboardPreview } from './formMappers';
import type { CrewDocumentRow, CrewLookups, LookupItem } from './types';
import CrewDashboard from './CrewDashboard';
import CrewPhotoSignaturePanel, { type CrewMediaCommitHandle } from './CrewPhotoSignaturePanel';
import CrewRecordsSections from './CrewRecordsPanel';
import type { RecordSection } from './CrewRecordsPanel';
import CrewHistoryPanel from './CrewHistoryPanel';

const RECORD_HEADER_ADD: Record<string, { label: string; section: RecordSection }> = {
  certs: { label: 'Add document', section: 'certs' },
  allottees: { label: 'Add allottee', section: 'allottees' },
  nok: { label: 'Add next of kin', section: 'nextOfKin' },
  appraisals: { label: 'Add appraisal', section: 'appraisals' },
  feedback: { label: 'Add feedback', section: 'feedback' },
  medicals: { label: 'Add medical', section: 'medicals' },
};

const formatFileSize = (bytes: number | null | undefined) => {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileExtensionLabel = (doc: CrewDocumentRow) => {
  const fromName = doc.originalFilename?.split('.').pop()?.trim();
  if (fromName && fromName !== doc.originalFilename && fromName.length <= 8) {
    return fromName.toUpperCase();
  }
  const mime = doc.mimeType?.split('/').pop()?.trim();
  if (mime) return mime.toUpperCase().slice(0, 8);
  return 'FILE';
};

const formatKindLabel = (kind: string | null | undefined) => {
  if (!kind?.trim()) return 'General';
  return kind
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const displayText = (value: string | null | undefined) => (value?.trim() ? value : '-');
const displayDate = (value: string | null | undefined) => (value?.trim() ? formatDateGlobal(value) : '-');
const displayBool = (value: boolean | undefined) => (value ? 'Yes' : 'No');

export interface CrewFileTabsProps {
  form: CrewFormState;
  setForm: React.Dispatch<React.SetStateAction<CrewFormState>>;
  lookups: CrewLookups | undefined;
  readOnly?: boolean;
  crewId?: number;
  documents?: CrewDocumentRow[];
  /** When editing, used to flush deferred photo/signature on form Save. */
  mediaRef?: React.Ref<CrewMediaCommitHandle>;
  activeSection?: string;
  onActiveSectionChange?: (value: string) => void;
  /** Manning-agent portal: per-tab visibility and write flags. */
  sectionAccess?: Record<
    string,
    { visible: boolean; canModify: boolean; canAdd: boolean; canDelete: boolean }
  >;
  /** Manning-agent portal: hide sensitive scalars. */
  agencyMode?: boolean;
}

const gridClass = 'grid md:grid-cols-2 lg:grid-cols-3 gap-4';

const CREW_NAV_GROUPS: {
  title: string;
  sections: {
    value: string;
    label: string;
    description?: string;
    countKey?: keyof CrewFormState;
  }[];
}[] = [
  {
    title: 'Overview',
    sections: [
      { value: 'dashboard', label: 'Dashboard', description: 'Summary and key crew metrics' },
    ],
  },
  {
    title: 'Profile',
    sections: [
      { value: 'personal', label: 'Personal', description: 'Identity, rank, and biographical details' },
      { value: 'contact', label: 'Contact', description: 'Phone, email, and addresses' },
      { value: 'availability', label: 'Availability', description: 'Employment status and manning' },
    ],
  },
  {
    title: 'Records',
    sections: [
      { value: 'certs', label: 'Documents', countKey: 'certs', description: 'Licenses and certifications' },
      { value: 'allottees', label: 'Allottees', countKey: 'allottees', description: 'Salary allotments' },
      { value: 'nok', label: 'Next of kin', countKey: 'nextOfKin', description: 'Emergency contacts' },
      { value: 'appraisals', label: 'Appraisals', countKey: 'appraisals', description: 'Evaluations, appraisals, discharge and briefing forms' },
      { value: 'feedback', label: 'Feedback', countKey: 'feedback', description: 'Feedback records' },
      { value: 'services', label: 'Services', description: 'Previous service and company services' },
      { value: 'medicals', label: 'Medical', countKey: 'medicals', description: 'Fitness and medical exams' },
      { value: 'documents', label: 'Other Files', description: 'Uploaded crew files' },
      { value: 'replacements', label: 'Audit Log', description: 'Crew file and crew-change audit log' },
    ],
  },
];

const ALL_CREW_SECTIONS = CREW_NAV_GROUPS.flatMap((group) => group.sections);

const SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  dashboard: ChartTrendingUpIcon,
  personal: UserIcon,
  contact: MapPinIcon,
  availability: CalendarIcon,
  certs: DocumentTextIcon,
  allottees: CoinIcon,
  nok: UserPlusIcon,
  appraisals: StarIcon,
  feedback: BellIcon,
  services: ShipIcon,
  replacements: RefreshIcon,
  medicals: CheckCircleIcon,
  documents: FolderIcon,
};

const FIELD_GROUP_BARS: Record<string, string> = {
  'Identity & reference': 'border-l-indigo-500',
  'Rank & nationality': 'border-l-sky-500',
  'Birth & family': 'border-l-rose-500',
  'Physical attributes': 'border-l-amber-500',
  'Phone & messaging': 'border-l-teal-500',
  Email: 'border-l-violet-500',
  Addresses: 'border-l-cyan-500',
  'Location & banking': 'border-l-blue-500',
  'Availability status': 'border-l-emerald-500',
  'Activity dates': 'border-l-orange-500',
  'Notes & duties': 'border-l-fuchsia-500',
};

const DEFAULT_FIELD_GROUP_BAR = 'border-l-slate-400';

const sectionCount = (form: CrewFormState, countKey?: keyof CrewFormState): number | null => {
  if (!countKey) return null;
  const value = form[countKey];
  return Array.isArray(value) ? value.length : null;
};

const CrewFileTabs: React.FC<CrewFileTabsProps> = ({
  form,
  setForm,
  lookups,
  readOnly: readOnlyProp = false,
  crewId = 0,
  documents: documentsProp = [],
  mediaRef,
  activeSection: activeSectionProp,
  onActiveSectionChange,
  sectionAccess: sectionAccessProp,
  agencyMode = false,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const sectionAccess = sectionAccessProp ?? lookups?.sectionAccess;
  const serviceSourceVisible = (section: 'prevService' | 'transactions') =>
    !sectionAccess || sectionAccess[section]?.visible !== false;
  const navGroups = React.useMemo(() => {
    const sectionVisible = (value: string) => {
      if (!sectionAccess) return true;
      if (value === 'services') {
        return sectionAccess.prevService?.visible !== false || sectionAccess.transactions?.visible !== false;
      }
      return sectionAccess[value]?.visible !== false;
    };
    const groups = !sectionAccess
      ? CREW_NAV_GROUPS
      : CREW_NAV_GROUPS.map((group) => ({
          ...group,
          sections: group.sections.filter((section) => sectionVisible(section.value)),
        })).filter((group) => group.sections.length > 0);
    if (!agencyMode) return groups;
    // Dashboard (AI overview) is office-only for agency accounts.
    return groups
      .map((group) => ({
        ...group,
        sections: group.sections.filter((section) => section.value !== 'dashboard'),
      }))
      .filter((group) => group.sections.length > 0);
  }, [sectionAccess, agencyMode]);
  const visibleSections = React.useMemo(
    () => navGroups.flatMap((group) => group.sections),
    [navGroups],
  );
  const [internalSection, setInternalSection] = React.useState(
    () => visibleSections[0]?.value ?? 'personal',
  );
  const activeSection = activeSectionProp ?? internalSection;
  const setActiveSection = (value: string) => {
    if (activeSectionProp == null) setInternalSection(value);
    onActiveSectionChange?.(value);
  };

  React.useEffect(() => {
    if (!visibleSections.some((s) => s.value === activeSection)) {
      const fallback = visibleSections[0]?.value ?? 'personal';
      setActiveSection(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSections, activeSection]);

  const sectionFlags = sectionAccess?.[activeSection];
  const readOnly =
    readOnlyProp ||
    Boolean(
      sectionFlags?.visible &&
        !sectionFlags.canModify &&
        !sectionFlags.canAdd &&
        !sectionFlags.canDelete,
    );
  const canAddRows = !readOnly && (sectionFlags == null || sectionFlags.canAdd);
  const canDeleteRows = !readOnly && (sectionFlags == null || sectionFlags.canDelete);
  const [recordsAddRequest, setRecordsAddRequest] = React.useState<{
    section: RecordSection;
    nonce: number;
  } | null>(null);
  const [documents, setDocuments] = React.useState<CrewDocumentRow[]>(documentsProp);
  const [docUploading, setDocUploading] = React.useState(false);
  const [docError, setDocError] = React.useState('');
  const [docDragActive, setDocDragActive] = React.useState(false);
  const [documentDelete, setDocumentDelete] = React.useState<CrewDocumentRow | null>(null);
  const [docDeleteBusy, setDocDeleteBusy] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const activeMeta = visibleSections.find((section) => section.value === activeSection);
  const headerAdd = !readOnly && canAddRows ? RECORD_HEADER_ADD[activeSection] : undefined;

  const dashboardPreview = React.useMemo(
    () => formToDashboardPreview(form, lookups, crewId),
    [form, lookups, crewId],
  );

  React.useEffect(() => {
    setDocuments(documentsProp);
  }, [documentsProp]);

  const readOnlyTextClass = cn('text-sm block min-h-[1.25rem]', colors.text);

  const applyForm = (updater: (state: CrewFormState) => CrewFormState | Record<string, unknown>) => {
    setForm((state) => updater(state) as CrewFormState);
  };

  const onHeaderAddClick = () => {
    if (!headerAdd) return;
    setRecordsAddRequest({ section: headerAdd.section, nonce: Date.now() });
  };

  const renderTextField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
    options?: { required?: boolean; placeholder?: string; multiline?: boolean; span?: boolean },
  ) => (
    <div className={cn('min-w-0', options?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={readOnly ? undefined : id} required={options?.required}>
        {label}
      </Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : options?.multiline ? (
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
          rows={3}
        />
      ) : (
        <TextInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
        />
      )}
    </div>
  );

  const lookupItemLabel = (items: LookupItem[] | undefined, value: string) => {
    const v = value.trim();
    if (!v) return '-';
    const item = items?.find((i) => String(i.id) === v);
    if (!item) return v;
    return item.abrv ? `${item.name} (${item.abrv})` : item.name;
  };

  const lookupFieldLabel = (SelectComp: React.ComponentType<LookupSelectProps>, value: string) => {
    if (SelectComp === SexTypeSelect) return lookupItemLabel(lookups?.sexTypes, value);
    if (SelectComp === RankSelect) return lookupItemLabel(lookups?.ranks, value);
    if (SelectComp === NationalitySelect) return lookupItemLabel(lookups?.nationalities, value);
    if (SelectComp === ManningAgentSelect) return lookupItemLabel(lookups?.manningAgents, value);
    if (SelectComp === MaritalStatusSelect) return lookupItemLabel(lookups?.maritalStatuses, value);
    if (SelectComp === ReligionSelect) return lookupItemLabel(lookups?.religions, value);
    if (SelectComp === AvailabilityStatusSelect) return lookupItemLabel(lookups?.availabilityStatuses, value);
    if (SelectComp === CountrySelect) {
      const v = value.trim();
      if (!v) return '-';
      const country = lookups?.countries?.find((c) => String(c.id) === v);
      if (!country) return v;
      return country.isoCode2 ? `${country.name} (${country.isoCode2})` : country.name;
    }
    if (SelectComp === PortSelect) {
      const v = value.trim();
      if (!v) return '-';
      const port = lookups?.ports?.find((p) => String(p.id) === v);
      if (!port) return v;
      return port.code ? `${port.name} (${port.code})` : port.name;
    }
    return value.trim() || '-';
  };

  const renderLookupField = (
    label: string,
    id: string,
    value: string,
    SelectComp: React.ComponentType<LookupSelectProps>,
    onChange: (value: string) => void,
    fieldOptions?: { required?: boolean; placeholder?: string; span?: boolean },
  ) => (
    <div className={cn('min-w-0', fieldOptions?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={readOnly ? undefined : id} required={fieldOptions?.required}>
        {label}
      </Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{lookupFieldLabel(SelectComp, value)}</span>
      ) : (
        <SelectComp
          name={id}
          value={value}
          includeIds={[value]}
          onChange={(e) => onChange(String(e.target.value))}
          placeholder={fieldOptions?.placeholder ?? 'Select…'}
          showClearButton
        />
      )}
    </div>
  );

  const renderDateField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={readOnly ? undefined : id}>{label}</Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayDate(value)}</span>
      ) : (
        <DateInput id={id} value={value} onChange={(e) => onChange(e.target.value)} showFormatHint={false} />
      )}
    </div>
  );

  const renderNumberField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={readOnly ? undefined : id}>{label}</Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : (
        <NumberInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value != null ? String(e.target.value) : '')}
        />
      )}
    </div>
  );

  const renderCheckboxField = (
    label: string,
    id: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
  ) => (
    <div className="min-w-0 flex items-end pb-1">
      {readOnly ? (
        <div>
          <Label>{label}</Label>
          <span className={readOnlyTextClass}>{displayBool(checked)}</span>
        </div>
      ) : (
        <label htmlFor={id} className={cn('flex items-center gap-2 text-sm cursor-pointer', colors.text)}>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          {label}
        </label>
      )}
    </div>
  );

  const renderFieldGroup = (
    title: string,
    description: string | undefined,
    children: React.ReactNode,
    options?: { contentClassName?: string },
  ) => {
    const bar = FIELD_GROUP_BARS[title] ?? DEFAULT_FIELD_GROUP_BAR;
    return (
      <section className={cn('rounded-lg border shadow-sm overflow-hidden border-l-4', colors.surface, colors.border, bar)}>
        <div className={cn('border-b px-5 py-3', colors.border, colors.headerBg)}>
          <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
          {description && (
            <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>
          )}
        </div>
        <div className={cn('p-5', options?.contentClassName ?? gridClass)}>{children}</div>
      </section>
    );
  };

  const confirmDeleteDocument = async () => {
    if (!crewId || !documentDelete) return;
    setDocDeleteBusy(true);
    setDocError('');
    try {
      await api.delete(`/crew/${crewId}/files/${documentDelete.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== documentDelete.id));
      setDocumentDelete(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setDocError(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Delete failed.');
    } finally {
      setDocDeleteBusy(false);
    }
  };

  const uploadDocuments = async (files: FileList | null) => {
    if (!crewId || !files?.length) return;
    setDocUploading(true);
    setDocError('');
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('files', file));
      const { data } = await api.post(`/crew/${crewId}/files`, formData);
      const created = Array.isArray(data) ? data : data ? [data] : [];
      setDocuments((prev) => [...prev, ...created]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setDocError(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setDocUploading(false);
    }
  };

  const downloadDocument = async (doc: CrewDocumentRow) => {
    if (!crewId) return;
    try {
      const response = await api.get(`/crew/${crewId}/files/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFilename || `crew-file-${doc.id}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setDocError('Download failed.');
    }
  };

  return (
    <Tabs
      value={activeSection}
      onValueChange={setActiveSection}
      className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden"
    >
      <nav
        className={cn(
          'flex lg:flex-col lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r min-h-0',
          colors.border,
          colors.surfaceHover,
        )}
        aria-label="Crew file sections"
      >
        <div
          className={cn(
            'flex lg:flex-col flex-1 min-h-0 gap-0.5 p-2 overflow-x-auto lg:overflow-y-auto dashboard-scrollbar',
            colors.surface,
          )}
        >
          {navGroups.map((group, groupIndex) => (
            <div
              key={group.title}
              className={cn(
                'flex lg:flex-col gap-0.5 shrink-0 lg:shrink',
                groupIndex > 0 && 'lg:mt-2 lg:pt-2 lg:border-t',
                groupIndex > 0 && colors.border,
              )}
            >
              <p
                className={cn(
                  'hidden lg:block px-2.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider',
                  colors.textMuted,
                )}
              >
                {group.title}
              </p>
              {group.sections.map((section) => {
                const isActive = activeSection === section.value;
                const count =
                  section.value === 'documents'
                    ? documents.length
                    : section.value === 'services'
                      ? (serviceSourceVisible('prevService') ? form.prevServices.length : 0) +
                        (serviceSourceVisible('transactions') ? form.transactions.length : 0)
                      : sectionCount(form, section.countKey);
                const Icon = SECTION_ICONS[section.value];
                return (
                  <button
                    key={section.value}
                    type="button"
                    title={section.description ?? section.label}
                    onClick={() => setActiveSection(section.value)}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm transition-colors shrink-0 lg:w-full text-left',
                      isActive
                        ? cn(colors.info, 'ring-1', colors.primaryBorderAll, 'font-semibold', colors.primaryFg)
                        : cn(colors.textSecondary, colors.interactiveHover),
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                        isActive
                          ? cn(colors.surface, colors.primaryFg)
                          : cn(colors.surfaceHover, colors.textMuted),
                      )}
                    >
                      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                    </span>
                    <span className={cn('min-w-0 flex-1 truncate', isActive ? colors.primaryFg : colors.text)}>
                      {section.label}
                    </span>
                    {count != null && (
                      <span
                        className={cn(
                          'inline-flex min-w-[1.35rem] items-center justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                          isActive
                            ? cn(colors.surface, colors.primaryFg)
                            : count > 0
                              ? cn(colors.surfaceHover, colors.textSecondary)
                              : cn(colors.textMuted),
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </nav>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeSection !== 'dashboard' && (
        <div
          className={cn(
            'flex-shrink-0 px-5 py-4 border-b',
            colors.headerBg,
            colors.border,
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className={cn('text-lg font-semibold', colors.text)}>
                {activeMeta?.label ?? 'Section'}
              </h2>
              {activeMeta?.description && (
                <p className={cn('text-sm mt-0.5', colors.textMuted)}>{activeMeta.description}</p>
              )}
            </div>
            {headerAdd && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-shrink-0 gap-1"
                onClick={onHeaderAddClick}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                {headerAdd.label}
              </Button>
            )}
          </div>
        </div>
        )}

        <div
          className={cn(
            'flex-1 overflow-y-auto dashboard-scrollbar min-h-0 px-5 pb-5',
            activeSection === 'services' ? 'pt-2' : 'pt-5',
          )}
        >
      {crewId > 0 ? (
        <div className={cn('mb-5', activeSection !== 'personal' && 'hidden')}>
          <CrewPhotoSignaturePanel ref={mediaRef} crewId={crewId} readOnly={readOnly} />
        </div>
      ) : !readOnly && activeSection === 'personal' ? (
        <p className={cn('mb-5 text-xs rounded-lg border px-4 py-3', colors.border, colors.textMuted)}>
          Save the crew file first to upload a photo and signature.
        </p>
      ) : null}
      <TabsContent value="dashboard" className="mt-0">
        <CrewDashboard item={dashboardPreview} preview={!readOnly} />
      </TabsContent>

      <TabsContent value="personal" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Identity & reference',
            'Legal name and internal crew reference',
            <>
              {renderTextField('Last name', 'crew-lastName', form.lastName, (v) => applyForm((s) => ({ ...s, lastName: v })), {
                required: true,
              })}
              {renderTextField('Middle name', 'crew-middleName', form.middleName ?? '', (v) =>
                applyForm((s) => ({ ...s, middleName: v })),
              )}
              {renderTextField('First name', 'crew-firstName', form.firstName, (v) => applyForm((s) => ({ ...s, firstName: v })), {
                required: true,
              })}
              {renderTextField('Crew ref', 'crew-crewRef', form.crewRef ?? '', (v) => applyForm((s) => ({ ...s, crewRef: v })))}
              {renderLookupField('Sex', 'crew-sexTypeId', form.sexTypeId, SexTypeSelect, (v) =>
                applyForm((s) => ({ ...s, sexTypeId: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Rank & nationality',
            'Position, nationality, and manning agent',
            <>
              {renderLookupField('Rank', 'crew-rankId', form.rankId, RankSelect, (v) =>
                applyForm((s) => ({ ...s, rankId: v })),
              )}
              {renderLookupField(
                'Nationality',
                'crew-nationalityId',
                form.nationalityId,
                NationalitySelect,
                (v) => applyForm((s) => ({ ...s, nationalityId: v })),
              )}
              {!agencyMode &&
                renderLookupField(
                  'Manning agent',
                  'crew-manningAgentId',
                  form.manningAgentId,
                  ManningAgentSelect,
                  (v) => applyForm((s) => ({ ...s, manningAgentId: v })),
                )}
            </>,
          )}
          {renderFieldGroup(
            'Birth & family',
            'Birth details, parents, and marital information',
            <>
              {renderDateField('Birth date', 'crew-birthDate', form.birthDate, (v) => applyForm((s) => ({ ...s, birthDate: v })))}
              {renderTextField('Birth place', 'crew-birthPlace', form.birthPlace ?? '', (v) =>
                applyForm((s) => ({ ...s, birthPlace: v })),
              )}
              {renderTextField('Father name', 'crew-fatherName', form.fatherName ?? '', (v) =>
                applyForm((s) => ({ ...s, fatherName: v })),
              )}
              {renderTextField('Mother name', 'crew-motherName', form.motherName ?? '', (v) =>
                applyForm((s) => ({ ...s, motherName: v })),
              )}
              {renderLookupField(
                'Marital status',
                'crew-maritalStatusId',
                form.maritalStatusId,
                MaritalStatusSelect,
                (v) => applyForm((s) => ({ ...s, maritalStatusId: v })),
              )}
              {renderLookupField(
                'Religion',
                'crew-religionId',
                form.religionId,
                ReligionSelect,
                (v) => applyForm((s) => ({ ...s, religionId: v })),
              )}
              {renderTextField('Wife name', 'crew-wifeName', form.wifeName ?? '', (v) => applyForm((s) => ({ ...s, wifeName: v })))}
              {renderNumberField('No. of children', 'crew-noOfChildren', String(form.noOfChildren ?? ''), (v) =>
                applyForm((s) => ({ ...s, noOfChildren: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Physical attributes',
            'Appearance and uniform sizes',
            <>
              {renderTextField('Eye color', 'crew-eyeColor', form.eyeColor ?? '', (v) => applyForm((s) => ({ ...s, eyeColor: v })))}
              {renderTextField('Hair color', 'crew-hairColor', form.hairColor ?? '', (v) =>
                applyForm((s) => ({ ...s, hairColor: v })),
              )}
              {renderTextField('Height', 'crew-crewHeight', form.crewHeight ?? '', (v) =>
                applyForm((s) => ({ ...s, crewHeight: v })),
              )}
              {renderTextField('Weight', 'crew-crewWeight', form.crewWeight ?? '', (v) =>
                applyForm((s) => ({ ...s, crewWeight: v })),
              )}
              {renderTextField('Shoes size', 'crew-shoesSize', form.shoesSize ?? '', (v) =>
                applyForm((s) => ({ ...s, shoesSize: v })),
              )}
              {renderTextField('Working clothes size', 'crew-workingClothesSize', form.workingClothesSize ?? '', (v) =>
                applyForm((s) => ({ ...s, workingClothesSize: v })),
              )}
            </>,
          )}
        </div>
      </TabsContent>

      <TabsContent value="contact" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Phone & messaging',
            'Telephone, mobile, fax, and Skype',
            <>
              {renderTextField('Telephone 1', 'crew-telephone1', form.telephone1 ?? '', (v) =>
                applyForm((s) => ({ ...s, telephone1: v })),
              )}
              {renderTextField('Telephone 2', 'crew-telephone2', form.telephone2 ?? '', (v) =>
                applyForm((s) => ({ ...s, telephone2: v })),
              )}
              {renderTextField('Fax', 'crew-fax1', form.fax1 ?? '', (v) => applyForm((s) => ({ ...s, fax1: v })))}
              {renderTextField('Mobile 1', 'crew-mobile1', form.mobile1 ?? '', (v) => applyForm((s) => ({ ...s, mobile1: v })))}
              {renderTextField('Mobile 2', 'crew-mobile2', form.mobile2 ?? '', (v) => applyForm((s) => ({ ...s, mobile2: v })))}
              {renderTextField('Skype', 'crew-skypeAccount', form.skypeAccount ?? '', (v) =>
                applyForm((s) => ({ ...s, skypeAccount: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Email',
            'Primary and secondary email addresses',
            <>
              {renderTextField('Email 1', 'crew-email1', form.email1 ?? '', (v) => applyForm((s) => ({ ...s, email1: v })))}
              {renderTextField('Email 2', 'crew-email2', form.email2 ?? '', (v) => applyForm((s) => ({ ...s, email2: v })))}
            </>,
          )}
          {renderFieldGroup(
            'Addresses',
            'Home, temporary, and country of residence',
            <>
              {renderTextField('Home address', 'crew-homeAddress', form.homeAddress ?? '', (v) =>
                applyForm((s) => ({ ...s, homeAddress: v })),
                { multiline: true, span: true },
              )}
              {renderTextField('Temporary address', 'crew-tempAddress', form.tempAddress ?? '', (v) =>
                applyForm((s) => ({ ...s, tempAddress: v })),
                { multiline: true, span: true },
              )}
              {renderLookupField('Country', 'crew-countryId', form.countryId, CountrySelect, (v) =>
                applyForm((s) => ({ ...s, countryId: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Location & banking',
            'Nearest travel points and bank account',
            <>
              {renderLookupField(
                'Nearest port',
                'crew-nearestPortId',
                form.nearestPortId,
                PortSelect,
                (v) => applyForm((s) => ({ ...s, nearestPortId: v })),
              )}
              {renderTextField('Nearest airport', 'crew-nearestAirport', form.nearestAirport ?? '', (v) =>
                applyForm((s) => ({ ...s, nearestAirport: v })),
              )}
              {!agencyMode &&
                renderTextField('Bank account', 'crew-bankAccount', form.bankAccount ?? '', (v) =>
                  applyForm((s) => ({ ...s, bankAccount: v })),
                )}
            </>,
          )}
        </div>
      </TabsContent>

      <TabsContent value="availability" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Availability status',
            'Current employment availability window',
            <>
              {renderLookupField(
                'Availability status',
                'crew-availabilityStatusId',
                form.availabilityStatusId,
                AvailabilityStatusSelect,
                (v) => applyForm((s) => ({ ...s, availabilityStatusId: v })),
              )}
              {renderDateField('Available from', 'crew-availableFrom', form.availableFrom, (v) =>
                applyForm((s) => ({ ...s, availableFrom: v })),
              )}
              {renderDateField('Available to', 'crew-availableTo', form.availableTo, (v) =>
                applyForm((s) => ({ ...s, availableTo: v })),
              )}
              {!agencyMode &&
                renderCheckboxField('Not recommended', 'crew-notRecommended', form.notRecommended ?? false, (v) =>
                  applyForm((s) => ({ ...s, notRecommended: v })),
                )}
            </>,
          )}
          {renderFieldGroup(
            'Activity dates',
            'Last application and contact dates',
            <>
              {renderDateField('Last application date', 'crew-lastApplicationDate', form.lastApplicationDate, (v) =>
                applyForm((s) => ({ ...s, lastApplicationDate: v })),
              )}
              {renderDateField('Last contacted date', 'crew-lastContactedDate', form.lastContactedDate, (v) =>
                applyForm((s) => ({ ...s, lastContactedDate: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Notes & duties',
            'Duties and free-text notes about availability',
            <>
              {renderTextField('Duties', 'crew-duties', form.duties ?? '', (v) => applyForm((s) => ({ ...s, duties: v })), {
                span: true,
              })}
              {renderTextField(
                'Availability notes',
                'crew-availabilityNotes',
                form.availabilityNotes ?? '',
                (v) => applyForm((s) => ({ ...s, availabilityNotes: v })),
                { multiline: true, span: true },
              )}
              {!agencyMode &&
                renderTextField('Crew notes', 'crew-crewNotes', form.crewNotes ?? '', (v) =>
                  applyForm((s) => ({ ...s, crewNotes: v })),
                  { multiline: true, span: true },
                )}
            </>,
          )}
        </div>
      </TabsContent>

      <CrewRecordsSections
        form={form}
        setForm={setForm}
        lookups={lookups}
        readOnly={readOnly}
        allowAdd={canAddRows}
        allowDelete={canDeleteRows}
        crewId={crewId}
        documents={documents}
        onDocumentsChange={setDocuments}
        addRequest={recordsAddRequest}
        onAddRequestHandled={() => setRecordsAddRequest(null)}
        hideInlineAddButtons
        serviceAccess={
          sectionAccess
            ? {
                showPrevious: serviceSourceVisible('prevService'),
                showCompany: serviceSourceVisible('transactions'),
                previous: {
                  canModify: !!sectionAccess.prevService?.canModify,
                  canAdd: !!sectionAccess.prevService?.canAdd,
                  canDelete: !!sectionAccess.prevService?.canDelete,
                },
                company: {
                  canModify: !!sectionAccess.transactions?.canModify,
                  canAdd: !!sectionAccess.transactions?.canAdd,
                  canDelete: !!sectionAccess.transactions?.canDelete,
                },
              }
            : undefined
        }
      />

      <TabsContent value="documents" className="mt-0">
        {!crewId ? (
          <div
            className={cn(
              'flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-12 text-center',
              colors.border,
              colors.surface,
            )}
          >
            <div
              className={cn(
                'mb-3 flex h-12 w-12 items-center justify-center rounded-xl border',
                colors.border,
                colors.surfaceHover,
              )}
            >
              <FolderIcon className={cn('h-5 w-5', colors.textMuted)} />
            </div>
            <p className={cn('text-sm font-semibold', colors.text)}>Save the crew file first</p>
            <p className={cn('mt-1 max-w-sm text-xs', colors.textMuted)}>
              Other files can be uploaded after this crew record exists.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className={cn(
                'flex flex-wrap items-start justify-between gap-3 rounded-2xl border px-4 py-3.5',
                colors.border,
                colors.surface,
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className={cn('text-sm font-semibold', colors.text)}>Other Files</h3>
                  <span
                    className={cn(
                      'inline-flex min-w-[1.5rem] items-center justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                      colors.surfaceHover,
                      colors.textSecondary,
                    )}
                  >
                    {documents.length}
                  </span>
                </div>
                <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                  General crew file attachments — PDFs, Office files, and images.
                </p>
              </div>
              {!readOnly && (
                <Button
                  type="button"
                  size="sm"
                  disabled={docUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0"
                >
                  <UploadCloudIcon className="h-3.5 w-3.5" />
                  {docUploading ? 'Uploading…' : 'Upload files'}
                </Button>
              )}
            </div>

            {!readOnly && (
              <>
                <input
                  ref={fileInputRef}
                  id="crew-doc-upload"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => void uploadDocuments(e.target.files)}
                />
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Upload other files"
                  onClick={() => {
                    if (!docUploading) fileInputRef.current?.click();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!docUploading) fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragActive(true);
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragActive(false);
                    if (!docUploading) void uploadDocuments(e.dataTransfer.files);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl border-2 border-dashed px-4 py-4 text-left transition sm:gap-4 sm:px-5',
                    colors.border,
                    docUploading && 'pointer-events-none opacity-70',
                    docDragActive
                      ? cn(colors.primaryBorderAll, colors.info, 'shadow-sm')
                      : colors.interactiveHover,
                  )}
                >
                  <div
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition',
                      docDragActive
                        ? cn(colors.info, colors.primaryFg)
                        : cn(colors.surfaceHover, colors.textMuted),
                    )}
                  >
                    <UploadCloudIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm font-semibold', colors.text)}>
                      {docUploading
                        ? 'Uploading…'
                        : docDragActive
                          ? 'Release to upload'
                          : 'Drop files here to upload'}
                    </p>
                    <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                      or click to browse · multiple files supported
                    </p>
                  </div>
                  <span
                    className={cn(
                      'hidden sm:inline-flex shrink-0 items-center rounded-full border px-3 py-1.5 text-xs font-medium',
                      colors.border,
                      colors.surface,
                      colors.text,
                    )}
                  >
                    Choose files
                  </span>
                </div>
              </>
            )}

            {docError && (
              <div
                className={cn(
                  'rounded-xl border px-3 py-2 text-xs',
                  isDark
                    ? 'border-red-500/30 bg-red-950/30 text-red-300'
                    : 'border-red-200 bg-red-50 text-red-700',
                )}
              >
                {docError}
              </div>
            )}

            {documents.length === 0 ? (
              <div
                className={cn(
                  'flex min-h-[160px] flex-col items-center justify-center rounded-2xl border px-6 py-10 text-center',
                  colors.border,
                  colors.surface,
                )}
              >
                <DocumentTextIcon className={cn('mb-2 h-8 w-8', colors.textMuted)} />
                <p className={cn('text-sm font-medium', colors.text)}>No other files yet</p>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  {readOnly
                    ? 'Nothing has been uploaded for this crew member.'
                    : 'Upload contracts, scans, or supporting files for this crew file.'}
                </p>
              </div>
            ) : (
              <div
                className={cn(
                  'overflow-hidden rounded-2xl border shadow-sm',
                  colors.border,
                  colors.surface,
                )}
              >
                <div
                  className={cn(
                    'hidden sm:grid grid-cols-[minmax(0,1fr)_7rem_5.5rem_7rem_5.5rem] gap-3 border-b px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider',
                    colors.border,
                    colors.headerBg,
                    colors.textMuted,
                  )}
                >
                  <span>File</span>
                  <span>Kind</span>
                  <span>Size</span>
                  <span>Uploaded</span>
                  <span className="text-right">Actions</span>
                </div>
                <ul>
                  {documents.map((doc, index) => {
                    const ext = fileExtensionLabel(doc);
                    const sizeLabel = formatFileSize(doc.sizeBytes);
                    const uploaded = displayDate(doc.createdAt);
                    return (
                      <li
                        key={doc.id}
                        className={cn(
                          'flex flex-col gap-3 px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1fr)_7rem_5.5rem_7rem_5.5rem] sm:items-center sm:gap-3',
                          index > 0 && 'border-t',
                          colors.border,
                          colors.interactiveHover,
                        )}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={cn(
                              'flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-xl border',
                              colors.border,
                              colors.surfaceHover,
                            )}
                            title={doc.mimeType || ext}
                          >
                            <DocumentTextIcon className={cn('h-4 w-4', colors.textSecondary)} />
                            <span className={cn('mt-0.5 text-[9px] font-bold tracking-wide', colors.textMuted)}>
                              {ext}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className={cn('truncate text-sm font-medium', colors.text)} title={doc.originalFilename}>
                              {doc.originalFilename}
                            </p>
                            {doc.notes ? (
                              <p className={cn('truncate text-xs', colors.textMuted)}>{doc.notes}</p>
                            ) : (
                              <p className={cn('truncate text-xs sm:hidden', colors.textMuted)}>
                                {[formatKindLabel(doc.kind), sizeLabel, uploaded !== '-' ? uploaded : null]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="hidden sm:inline">
                          <span
                            className={cn(
                              'inline-flex max-w-full truncate rounded-md px-2 py-0.5 text-[11px] font-medium',
                              colors.surfaceHover,
                              colors.textSecondary,
                            )}
                            title={formatKindLabel(doc.kind)}
                          >
                            {formatKindLabel(doc.kind)}
                          </span>
                        </span>
                        <span className={cn('hidden text-sm tabular-nums sm:inline', colors.textMuted)}>
                          {sizeLabel || '—'}
                        </span>
                        <span className={cn('hidden text-sm sm:inline', colors.textMuted)}>
                          {uploaded}
                        </span>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-full gap-1.5"
                            title="Download"
                            onClick={() => void downloadDocument(doc)}
                          >
                            <DownloadIcon className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Download</span>
                          </Button>
                          {!readOnly && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              title="Delete file"
                              aria-label="Delete file"
                              className={cn(
                                'h-8 w-8',
                                isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
                              )}
                              onClick={() => setDocumentDelete(doc)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </TabsContent>

      <TabsContent value="replacements" className="mt-0">
        <CrewHistoryPanel crewId={crewId} />
      </TabsContent>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={!!documentDelete}
        onClose={() => (!docDeleteBusy ? setDocumentDelete(null) : undefined)}
        onConfirm={() => void confirmDeleteDocument()}
        title="Delete file?"
        message="This permanently deletes the uploaded file from the crew record."
        details={documentDelete ? { File: documentDelete.originalFilename } : null}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={docDeleteBusy}
        zIndex={130}
      />
    </Tabs>
  );
};

export default CrewFileTabs;
